import { Injectable } from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';
import { SelectionModel } from '@angular/cdk/collections';

import { SelectedCustomerCacheInterface } from 'src/app/core/store/interface/selectedCustomerCache.interface';

export interface CustomerNode extends SelectedCustomerCacheInterface {
  nodeChildren?: CustomerNode[];
}

export interface CustomerFlatNode {
  expandable: boolean;
  name: string;
  level: number;
  item: SelectedCustomerCacheInterface;
}

@Injectable({
  providedIn: 'root'
})
export class TreeHelperService {
  /**
   * Build a nested tree from a flat array using uid/uidParent.
   */
  buildTree(flat: SelectedCustomerCacheInterface[]): CustomerNode[] {
    const idMap = new Map<string, CustomerNode>();
    const roots: CustomerNode[] = [];
    flat.forEach((item) => {
      idMap.set(item.uid, { ...item, nodeChildren: [] });
    });
    idMap.forEach((node, uid) => {
      const parentUid = node.uidParent;
      if (parentUid && idMap.has(parentUid)) {
        idMap.get(parentUid)!.nodeChildren!.push(node);
      } else {
        roots.push(node);
      }
    });
    return roots;
  }

  /**
   * Flattener for Angular Material Tree.
   */
  transformer = (node: CustomerNode, level: number): CustomerFlatNode => ({
    expandable: !!node.nodeChildren && node.nodeChildren.length > 0,
    name: node.name,
    level,
    item: node
  });

  getLevel = (node: CustomerFlatNode) => node.level;
  isExpandable = (node: CustomerFlatNode) => node.expandable;
  getChildren = (node: CustomerNode) => node.nodeChildren;
  hasChild = (_: number, node: CustomerFlatNode) => node.expandable;

  /**
   * Selection logic for checkboxes.
   */
  descendantsAllSelected(
    treeControl: FlatTreeControl<CustomerFlatNode>,
    selection: SelectionModel<CustomerFlatNode>,
    node: CustomerFlatNode
  ): boolean {
    const descendants = treeControl.getDescendants(node);
    return (
      descendants.length > 0 &&
      descendants.every((child) => selection.isSelected(child))
    );
  }

  descendantsPartiallySelected(
    treeControl: FlatTreeControl<CustomerFlatNode>,
    selection: SelectionModel<CustomerFlatNode>,
    node: CustomerFlatNode
  ): boolean {
    const descendants = treeControl.getDescendants(node);
    const result = descendants.some((child) => selection.isSelected(child));
    // return result && !this.descendantsAllSelected(treeControl, selection, node);
    const nodeIsSelected = selection.isSelected(node);
    // Show indeterminate if: some children selected AND (not all selected OR parent not selected)
    return result && (!this.descendantsAllSelected(treeControl, selection, node) || !nodeIsSelected);
  }

  customerSelectionToggle(
    treeControl: FlatTreeControl<CustomerFlatNode>,
    selection: SelectionModel<CustomerFlatNode>,
    node: CustomerFlatNode,
    getParentNode: (node: CustomerFlatNode) => CustomerFlatNode | null,
    isFiltered: boolean = false
  ): void {
    selection.toggle(node);

    // Always toggle descendants
    const descendants = treeControl.getDescendants(node);
    if (selection.isSelected(node)) {
      selection.select(...descendants);
    } else {
      selection.deselect(...descendants);
    }
    // Only check parents if not filtered
    if (!isFiltered) {
      this.checkAllParentsSelection(treeControl, selection, node, getParentNode);
    } else {
      // In filtered mode, deselect all ancestors
      let parent = getParentNode(node);
      while (parent) {
        selection.deselect(parent);
        parent = getParentNode(parent);
      }
    }
  }

  checkAllParentsSelection(
    treeControl: FlatTreeControl<CustomerFlatNode>,
    selection: SelectionModel<CustomerFlatNode>,
    node: CustomerFlatNode,
    getParentNode: (node: CustomerFlatNode) => CustomerFlatNode | null
  ): void {
    let parent: CustomerFlatNode | null = getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(treeControl, selection, parent);
      parent = getParentNode(parent);
    }
  }

  checkRootNodeSelection(
    treeControl: FlatTreeControl<CustomerFlatNode>,
    selection: SelectionModel<CustomerFlatNode>,
    node: CustomerFlatNode
  ): void {
    const nodeSelected = selection.isSelected(node);
    const descendants = treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => selection.isSelected(child));
    if (nodeSelected && !descAllSelected) {
      selection.deselect(node);
    } 
    // Commented out: auto-select parent when all children are selected
    // else if (!nodeSelected && descAllSelected) {
    //   selection.select(node);
    // }
  }

  getParentNode(
    treeControl: FlatTreeControl<CustomerFlatNode>,
    node: CustomerFlatNode
  ): CustomerFlatNode | null {
    const currentLevel = node.level;
    if (currentLevel < 1) {
      return null;
    }
    const startIndex = treeControl.dataNodes.indexOf(node) - 1;
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = treeControl.dataNodes[i];
      if (currentNode.level < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  filterTree(customers: SelectedCustomerCacheInterface[], filterText: string): CustomerNode[] {
    if (!filterText) {
      return this.buildTree(customers);
    }
    const filter = filterText.toLowerCase();

    // Recursive function to filter nodes and keep ancestors
    const filterRecursive = (nodes: CustomerNode[]): CustomerNode[] => {
      return nodes
        .map(node => {
          const children = node.nodeChildren ? filterRecursive(node.nodeChildren) : [];
          const matches = node.name.toLowerCase().includes(filter);
          if (matches || children.length) {
            return { ...node, nodeChildren: children };
          }
          return null;
        })
        .filter(Boolean) as CustomerNode[];
    };

    // Build the full tree, then filter
    const fullTree = this.buildTree(customers);
    return filterRecursive(fullTree);
  }

  expandSearchedNodes(
    treeControl: FlatTreeControl<CustomerFlatNode>,
    getParentNode: (node: CustomerFlatNode) => CustomerFlatNode | null,
    searchValue: string): void {
    if (!searchValue) return;
    const lowerSearch = searchValue.toLowerCase();
    const matchingNodes = treeControl.dataNodes.filter(
      n => n.name && n.name.toLowerCase().includes(lowerSearch)
    );
    matchingNodes.forEach(node => {
      let parent = getParentNode(node);
      while (parent) {
        treeControl.expand(parent);
        parent = getParentNode(parent);
      }
    });
  }
}
